import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState, useRef, useMemo } from 'react';

import dayjs from 'dayjs';

import { useData } from '../../hooks/DataProvider.jsx';

import { Menu as MainMenu } from '../../components/Menu.jsx';
import { ModelShipment } from '../../models/ModelShipment.js';
import { CreatFieldComponent } from '../../components/Fields.jsx';
import { TierSelectBox } from '../../components/TierSelectBox.jsx';
import { ServiceList } from '../../components/ServiceList.jsx';

import DetailHandlingUnit from '../../components/DetailHandlingUnit.jsx';

import { generateId } from '../../scripts/utils.js';

import { Box, Button, Divider, Typography, LinearProgress, } from '@mui/material';

import { Incotermes, ShipmentDirections, ShipmentStatus, TransportMode, Currencies } from '../../scripts/configData.js';

import translations from '../../scripts/translations';

import { getShipements, updateShipement, deleteShipement } from '../../api/shipments.js';
import { getTypeOfGoods } from '../../api/typeOfGoods.js'
import { getAsingeIdx, deleteAsingeIdx } from '../../api/asingne_idx.js';

import isEqual from 'lodash.isequal';

import Popup from '../../components/Popup.jsx';

const Shipement = () => {

    const { shipmentId } = useParams();
    const { dataShipement, setDataShipement, initialDataShipement, setInitialDataShipement, language, dataServiceList, setDataServiceList } = useData();

    // const screenSizeIsSmall = useMediaQuery('(min-width:600px)');

    const [popupData, setPopupData] = useState({
        open: false,
        code: null,
        id: null,
        fn: null,
    });

    const topMenuRef = useRef(null);
    const [isTopMenuVisible, setIsTopMenuVisible] = useState(true);

    const [updateIds, setUpdateIds] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [handlingUnitsisOpen, setHandlingUnitsIsOpen] = useState(false);
    const [typeOfGoods, setTypeOfGoods] = useState([]);
    const [openServiceList, setOpenServiceList] = useState(false);
    const [dataAsingeIdx, setDataAsingeIdx] = useState([]);

    const isInitialLoad = useRef(true);
    const navigate = useNavigate();

    const btns = translations.button[language];
    const page = translations.pages.shipment[language];

    const handleDuplicate = () => {
        const duplicateParams = {
            shipmentDirection: dataShipement.shipmentDirection || ModelShipment.shipmentDirection.default,
            transportMode: dataShipement.transportMode || ModelShipment.transportMode.default,
            shipmentStatus: dataShipement.shipmentStatus || ModelShipment.shipmentStatus.default,
            incotermCode: dataShipement.incotermCode || ModelShipment.incotermCode.default,
            orderingParty: dataShipement.orderingParty || '',
            consignor: dataShipement.consignor || '',
            consignee: dataShipement.consignee || '',
            transShipment: dataShipement.transShipment || '',
        };

        handleNew(duplicateParams);
    };

    const handleNew = (params = {}) => {

        const _id = generateId();
        const newShipement = {
            _id: _id,
            shipmentOrderCode: 'new',
            creationDate: dayjs().format('YYYY-MM-DD'),
            shipmentDirection: ModelShipment.shipmentDirection.default,
            transportMode: ModelShipment.transportMode.default,
            shipmentStatus: ModelShipment.shipmentStatus.default,
            incotermCode: ModelShipment.incotermCode.default,
            orderingParty: '',
            consignor: '',
            consignee: '',
            transShipment: '',
            totalHandlingUnits: 0,
            soWeight: 0,
            soChargeableWeight: 0,
            soVolume: 0,
            soLinearMeters: 0,
            soWeightToVolumeRatio: 0,
            handlingUnits: [],
            handlingIdx: 0,

            scheduledPickup: null,
            actualPickup: null,
            pickupOperator: {},

            scheduledDelivery: null,
            actualDelivery: null,
            deliveryOperator: {},

            internalRef: '',
            deliveryRef: '',
            customerRef: '',
            pickupNote: '',

            goodsDescription: '',
            declaredValue: 0,
            declaredCurrency: Currencies[0].key, // Default to first currency

            revenue: 0,
            cost: 0,
            commercialMargin: 0,
            serviceList: [],

            shipmentNote: '',

            isInvoiceCreated: false,

            //

            createdAt: null,
            updatedAt: null,
            createdBy: '',
            updatedBy: '',

            grouppageIds: [],

            mrn: ''

        }
        // If params are provided, merge them into the new shipment
        Object.assign(newShipement, params);

        console.log('New Shipement:', newShipement);
        setDataShipement(newShipement);
        navigate(`/shipments/${_id}`);
    };

    const handleSave = async () => {
        setIsLoadingButton(true);
        const isNew = dataShipement.shipmentOrderCode === 'new';

        const payload = {
            ...dataShipement,
            serviceList: dataServiceList,
        };

        try {
            const rs = await updateShipement(structuredClone(payload));
            if (rs) {
                setDataShipement(rs);
                setDataServiceList(rs.serviceList || []);
                setInitialDataShipement(structuredClone(rs));

                if (isNew) {
                    navigate(`/shipments/${rs._id}`);
                }
            } else {
                console.error('Failed to update Shipement');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoadingButton(false);
        }

        setUpdateIds(prev => prev + 1);
    };

    const handleDelete = () => {

        if (dataShipement._id && dataShipement._id !== 'new') {
            console.log('Deleting Shipement:', dataShipement._id);
            // Implement delete logic here, e.g., deleteShipement(dataShipement._id).then(rs => { ... });
            deleteShipement(dataShipement._id).then(rs => {
                if (rs) {
                    console.log('Shipement deleted successfully:', rs);
                } else {
                    console.error('Failed to delete Shipement');
                }
            });
            setDataShipement({});
            navigate('/shipments');
        }
    }

    const handleDeletePopup = () => {
        setPopupData({
            open: true,
            code: 'deleteShipementConfirmation',
            id: null,
            fn: handleDelete,
            types: {
                hasCancel: true,
                hasConfirm: true,
            }
        });
    };

    const hasChanged = useMemo(() => {
        if (isInitialLoad.current) return false;

        const current = { ...dataShipement };
        const initial = { ...initialDataShipement };

        return !isEqual(current, initial);
    }, [dataShipement, dataServiceList, initialDataShipement]);

    // Log differences between dataShipement+dataServiceList and initialDataShipement+initialServiceList
    useEffect(() => {
        const current = { ...dataShipement };
        const initial = { ...initialDataShipement };

        const getDiff = (a, b) => {
            const diffs = {};
            for (const key in a) {
                if (!isEqual(a[key], b[key])) {
                    diffs[key] = { current: a[key], initial: b[key] };
                }
            }
            return diffs;
        };

        const differences = getDiff(current, initial);
        if (Object.keys(differences).length > 0) {
            console.log("Differences detected:", differences);
        }
    }, [dataShipement, dataServiceList, initialDataShipement]);


    const handelDeleteAsingeIdx = (consolidation_id ) => {
      
        deleteAsingeIdx({ shipment_id: dataShipement._id, consolidation_id: consolidation_id }).then(rs => {
            if (rs) {
                console.log('Assignment deleted successfully:', rs);
                setDataAsingeIdx(prev => prev.filter(item => item.asingne_consolidations[0]._id !== consolidation_id));
            } else {
                console.error('Failed to delete assignment');
            }
        }).catch(err => {
            console.error('Error deleting assignment:', err);
        });
    }

    useEffect(() => {
        if (shipmentId === 'new') {
            handleNew();
            setIsLoading(false);
            isInitialLoad.current = true;
            setTimeout(() => {
                isInitialLoad.current = false;
            }, 0);
        } else if (shipmentId && shipmentId !== 'new') {
            getShipements({ _id: shipmentId }).then(rs => {
                if (rs?.length > 0) {
                    let result = rs[0];
                    delete result.updated_at;
                    const services = result.serviceList || [];
                    delete result.serviceList;
                    setDataServiceList(services);
                    setDataShipement(result);
                    const cloned = structuredClone(result);
                    setInitialDataShipement(cloned);
                    setIsLoading(false);
                    isInitialLoad.current = true;
                    setTimeout(() => {
                        isInitialLoad.current = false;
                    }, 0);
                } 
                else {
                    // setDataShipement({});
                    setDataServiceList([]);
                    setIsLoading(false);
                    isInitialLoad.current = true;
                    setTimeout(() => {
                        isInitialLoad.current = false;
                    }, 0);
                }
            });
        } else {
            setDataShipement({});
            setDataServiceList([]);
            setIsLoading(false);
            isInitialLoad.current = true;
            setTimeout(() => {
                isInitialLoad.current = false;
            }, 0);
        }
    }, [shipmentId, updateIds]);


    useEffect(() => {
        getTypeOfGoods().then(rs => {
            if (rs) {
                setTypeOfGoods(rs);
            } else {
                console.error('Failed to fetch type of goods');
            }
        });
    }, []);


    useEffect(() => {
        const el = topMenuRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsTopMenuVisible(entry.isIntersecting);
            },
            { root: null, threshold: 1 }
        );
        observer.observe(el);
        return () => observer.unobserve(el);
    }, []);

    useEffect(() => {
        if (dataShipement._id && dataShipement._id !== 'new') {
            getAsingeIdx({ shipment_id: dataShipement._id }).then(rs => {
                if (rs) {
                    console.log('Asingne Index Data:', rs);
                    setDataAsingeIdx(rs);
                } else {
                    console.error('Failed to fetch assignment index data');
                }
            });
        } else {
            setDataAsingeIdx([]);
        }
    }, [dataShipement._id, updateIds]);

    const sxDivider = {
        my: 2,
    };

    // const color = '#00000099';


    return (
        isLoading ? <LinearProgress /> : <>
            <MainMenu currentPage={'shipments'} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                {/* <h2>Tiers: {tierId}</h2> */}
                <Box>
                    {/* <Typography variant='overline'>{page.serviceList} :</Typography> */}

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr .7fr .7fr .7fr', gap: 2 }} >

                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0 }} >
                            <Typography variant='subtitle1' sx={{ fontSize: '1.3rem', mr: 1 }} >{page.shipment} : </Typography>
                            <Typography variant='subtitle1' sx={{ fontSize: '1.3rem' }} color={dataShipement?.shipmentOrderCode === 'new' ? 'warning' : 'primary'}  > {dataShipement?.shipmentOrderCode}</Typography>
                        </Box>
                    </Box>
                </Box>
                <Box ref={topMenuRef} sx={{ display: 'flex', gap: 1 }} >
                    <Button variant='contained' disabled={!hasChanged} loading={isLoadingButton} color='warning' onClick={handleSave}>{btns.save}</Button>
                    <Button variant='outlined' onClick={handleNew}>{btns.add}</Button>
                    <Button variant='outlined' onClick={handleDeletePopup} disabled={shipmentId === 'new' ? true : false} color="secondary" >{btns.delete}</Button>
                    <Button variant='outlined' onClick={handleDuplicate} disabled={shipmentId === 'new' ? true : false} >{btns.duplicate}</Button>
                    <Button variant='outlined' onClick={() => console.log(dataShipement)}>{btns.print}</Button>
                </Box>
            </Box>

            <Box>
                <Box sx={{ flex: 1, minWidth: '300px' }}>
                    <Divider sx={sxDivider} />
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr .6fr 1fr ', alignItems: 'center' }} >

                        <CreatFieldComponent field={ModelShipment.revenue} data={dataShipement} updateData={setDataShipement} disabledInForm />
                        <CreatFieldComponent field={ModelShipment.commercialMargin} data={dataShipement} updateData={setDataShipement} disabledInForm />
                        <CreatFieldComponent field={ModelShipment.cost} data={dataShipement} updateData={setDataShipement} disabledInForm />


                        <CreatFieldComponent field={ModelShipment.shipmentDirection} data={dataShipement} updateData={setDataShipement} options={ShipmentDirections} />
                        <CreatFieldComponent field={ModelShipment.shipmentStatus} data={dataShipement} updateData={setDataShipement} options={ShipmentStatus} />
                        <CreatFieldComponent field={ModelShipment.transportMode} data={dataShipement} updateData={setDataShipement} options={TransportMode} />
                        <CreatFieldComponent field={ModelShipment.incotermCode} data={dataShipement} updateData={setDataShipement} options={Incotermes} />
                        {dataShipement?.creationDate && (
                            <CreatFieldComponent field={ModelShipment.creationDate} data={dataShipement} updateData={setDataShipement} />
                        )}

                    </Box>

                    <Divider sx={sxDivider} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} >
                        <TierSelectBox model={ModelShipment} data={dataShipement} updateData={setDataShipement} type={'orderingParty'} />
                        <Divider style={{ paddingTop: '0px' }} />
                        <TierSelectBox model={ModelShipment} data={dataShipement} updateData={setDataShipement} type={'consignor'} />
                        <Divider sx={{ m: 0 }} />
                        <TierSelectBox model={ModelShipment} data={dataShipement} updateData={setDataShipement} type={'consignee'} />
                        <Divider sx={{}} />
                        <TierSelectBox model={ModelShipment} data={dataShipement} updateData={setDataShipement} type={'transShipment'} />
                    </Box>

                    <Divider sx={sxDivider} />

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr', alignItems: 'center' }} >
                        <CreatFieldComponent field={ModelShipment.totalHandlingUnits} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.soWeight} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.soVolume} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.soWeightToVolumeRatio} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.soLinearMeters} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.soChargeableWeight} data={dataShipement} updateData={setDataShipement} />

                        <Button variant='outlined' onClick={() => setHandlingUnitsIsOpen(!handlingUnitsisOpen)} >{page.detailsHu}</Button>
                        <Button variant='outlined' onClick={() => setOpenServiceList(!openServiceList)} >{page.serviceList}</Button>
                    </Box>

                    <Divider sx={sxDivider} />

                    {
                        handlingUnitsisOpen && <>
                            <DetailHandlingUnit dataShipement={dataShipement} setDataShipement={setDataShipement} language={language} />

                            <Divider sx={sxDivider} />
                        </>
                    }

                    {/* pickup */}


                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1.1fr 1.1fr 3fr', alignItems: 'center' }} >
                        <CreatFieldComponent field={ModelShipment.scheduledPickup} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.actualPickup} data={dataShipement} updateData={setDataShipement} />
                        <Box sx={{ ml: 4 }}>
                            <TierSelectBox model={ModelShipment} data={dataShipement} updateData={setDataShipement} type={'pickupOperator'} size={2} />
                        </Box>
                    </Box>

                    {/* / pickup */}

                    <Divider sx={sxDivider} />

                    {/* delivery */}

                    {/* <Typography variant='subtitle1' sx={{ fontSize: '1.1rem' }} >{page.delivery} :</Typography> */}
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1.1fr 1.1fr 3fr', alignItems: 'center' }} >

                        <CreatFieldComponent field={ModelShipment.scheduledDelivery} data={dataShipement} updateData={setDataShipement} />
                        <CreatFieldComponent field={ModelShipment.actualDelivery} data={dataShipement} updateData={setDataShipement} />
                        <Box sx={{ ml: 4 }}>
                            <TierSelectBox model={ModelShipment} data={dataShipement} updateData={setDataShipement} type={'deliveryOperator'} size={2} />
                        </Box>
                    </Box>

                    {/* /delivery */}

                    <Divider sx={sxDivider} />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.05fr 4fr', gap: 2, }} >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} >
                            <CreatFieldComponent field={ModelShipment.internalRef} data={dataShipement} updateData={setDataShipement} />
                            <CreatFieldComponent field={ModelShipment.deliveryRef} data={dataShipement} updateData={setDataShipement} />
                            <CreatFieldComponent field={ModelShipment.customerRef} data={dataShipement} updateData={setDataShipement} />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }} >
                            <Typography variant='overline' color='#00000099'>{page.pickupNote} :</Typography>
                            <CreatFieldComponent field={ModelShipment.pickupNote} data={dataShipement} updateData={setDataShipement} />
                        </Box>

                    </Box>

                    <Divider sx={sxDivider} />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '.5fr .5fr 1fr 1.5fr', gap: 2 }} >
                        <CreatFieldComponent field={ModelShipment.declaredValue} data={dataShipement} updateData={setDataShipement} errorCondition={dataShipement.declaredValue <= 0 ? true : false} />
                        <CreatFieldComponent field={ModelShipment.declaredCurrency} data={dataShipement} updateData={setDataShipement} options={Currencies} />
                        <CreatFieldComponent field={ModelShipment.goodsDescription} data={dataShipement} updateData={setDataShipement} options={typeOfGoods} />
                        <CreatFieldComponent field={ModelShipment.mrn} data={dataShipement} updateData={setDataShipement} />

                    </Box>

                    <Divider sx={sxDivider} />





                    {/* <Divider sx={sxDivider} /> */}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }} >
                        <Typography variant='overline' color='#00000099' >{page.shipmentNote} :</Typography>
                        <CreatFieldComponent field={ModelShipment.shipmentNote} data={dataShipement} updateData={setDataShipement} />
                    </Box>

                    <Divider sx={sxDivider} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} >
                        <Typography variant='overline' color='#00000099' >{btns.consolidations} :</Typography>
                        {
                            dataAsingeIdx.length > 0 && dataAsingeIdx.map((item, index) => {
                                let consolidation = item.asingne_consolidations[0] || {};
                                // [0].asingne_consolidations[0].consolidationCode
                                return (<Box key={index}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, 1fr)',
                                        gridTemplateRows: 'auto',
                                        gridAutoFlow: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                        border: '1px solid #00000022',
                                        borderRadius: 1, p: 1
                                    }} >

                                    <Typography variant='body2'>{consolidation.consolidationCode}</Typography>
                                    <Typography variant='body2'>{consolidation.creationDate ? dayjs(consolidation.creationDate).format('DD/MM/YYYY') : ''}</Typography>
                                    <Typography variant='body2'>{consolidation.departureDate ? dayjs(consolidation.departureDate).format('DD/MM/YYYY') : ''}</Typography>
                                    <Button variant='outlined' size='small' onClick={() => {
                                        navigate(`/consolidations/${consolidation._id}`);
                                    }} >{btns.open}</Button>
                                    {/* <Button variant='outlined' size='small' onClick={() => {
                                        setPopupData({
                                            open: true,
                                            code: 'deleteAsingneIdxConfirmation',
                                            id: item._id,
                                            fn: () =>  handelDeleteAsingeIdx(consolidation._id),
                                            types: {
                                                hasCancel: true,
                                                hasConfirm: true,
                                            }
                                        });
                                    }} >{btns.delete}</Button> */}

                                </Box>)
                            })
                        }
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }} ></Box>


                    <ServiceList openServiceList={openServiceList} setOpenServiceList={setOpenServiceList} handleSaveShipement={handleSave} isLoadingButton={isLoadingButton} hasChanged={hasChanged} />

                    {popupData.open && <Popup popupData={popupData} setPopupData={setPopupData} />}

                    {/* Кнопка SAVE */}
                    {!isTopMenuVisible && (
                        <Box sx={{
                            position: 'fixed',
                            bottom: '100px',
                            right: '5%',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'right',

                            zIndex: 1300, // соответствует MUI modal/dialog
                        }}>
                            <Button variant='contained' disabled={!hasChanged} loading={isLoadingButton} color='warning' onClick={handleSave}>{btns.save}</Button>
                        </Box>
                    )}



                </Box >
            </Box >

        </>
    );


}

export default Shipement;

